import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Building2,
  MapPin,
  Clock,
  ArrowRight,
  Check,
  Sparkles,
  Phone,
  Globe,
  ShoppingCart,
  Info,
  User,
} from 'lucide-react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Tooltip } from '@/components/ui/Tooltip'
import { CountrySelect, COUNTRIES } from '@/components/ui/CountrySelect'
import { useCreateRestaurant, useUpdateRestaurant, type POSMode } from '@/lib/queries/useRestaurants'
import { useCreateOpeningHour } from '@/lib/queries/useOpeningHours'
import { OnboardingFloorPlanEditor } from '@/components/floor-plan/OnboardingFloorPlanEditor'
import { useAppStore } from '@/lib/store/useAppStore'
import { useAuth } from '@/contexts/AuthContext'
import { strapiPut } from '@/lib/strapi'
import i18next, { applyRTL } from '@/lib/i18n'
import { toast } from 'sonner'

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'it', label: 'Italiano' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'ar', label: 'العربية' },
  { value: 'de', label: 'Deutsch' },
]

// ─── Schemas ─────────────────────────────────────────────────────────────────

const restaurantSchema = z.object({
  name: z.string().min(1, 'Restaurant name is required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().optional(),
  address: z.string().optional(),
  countryCode: z.string().optional(),
  country: z.string().optional(),
})
type RestaurantForm = z.infer<typeof restaurantSchema>

// ─── Constants ───────────────────────────────────────────────────────────────

// ISO weekday: 1=Monday … 7=Sunday (matches OpeningHourForm convention)
const WEEK_DAYS: { label: string; value: number }[] = [
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
  { label: 'Sunday', value: 7 },
]

interface DaySchedule {
  open: boolean
  openTime: string   // "HH:MM"
  closeTime: string  // "HH:MM"
}


interface FeatureState {
  sheAnswers: boolean
  customerPage: boolean
  pos: boolean
  posMode: POSMode
}

interface FeatureCardProps {
  icon: React.ElementType
  iconColor: string
  iconBg: string
  name: string
  tagline: string
  tooltip: string
  enabled: boolean
  onToggle: () => void
  badge?: string
}

function FeatureCard({ icon: Icon, iconColor, iconBg, name, tagline, tooltip, enabled, onToggle, badge }: FeatureCardProps) {
  return (
    <div
      onClick={onToggle}
      className={`relative flex items-start gap-4 p-4 rounded-[var(--radius-lg)] border cursor-pointer transition-all select-none ${
        enabled
          ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber-dim)]'
          : 'border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] hover:border-[var(--color-border-subtle)]'
      }`}
    >
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)] ${iconBg}`}>
        <Icon className={`h-5 w-5 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-semibold text-[var(--color-text-primary)]">{name}</span>
          {badge && (
            <span className="px-1.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide rounded bg-[var(--color-accent-amber)] text-[#1a1714]">
              {badge}
            </span>
          )}
          <Tooltip content={tooltip} side="top" className="whitespace-normal w-64 max-w-xs text-left">
            <span onClick={(e) => e.stopPropagation()}>
              <Info className="h-3.5 w-3.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] cursor-default" />
            </span>
          </Tooltip>
        </div>
        <p className="text-xs text-[var(--color-text-muted)] leading-relaxed">{tagline}</p>
      </div>
      <div className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors ${enabled ? 'bg-[var(--color-accent-amber)]' : 'bg-[var(--color-bg-base)]'}`}>
        <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-4' : 'translate-x-0'}`} />
      </div>
    </div>
  )
}

/** Convert "HH:MM" → HHMM integer (e.g. "09:30" → 930) */
function timeToHHMM(time: string): number {
  return parseInt(time.replace(':', ''), 10)
}

/** Convert "HH:MM" → total seconds from midnight */
function timeToSec(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return h * 3600 + m * 60
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function OnboardingPage() {
  const navigate = useNavigate()
  const { user, updateUser } = useAuth()
  const { restaurantId, setRestaurantId, setOnboardingCompleted, setFeatures, setLanguageCode } = useAppStore()

  // Resume from step 1 (restaurant) if restaurant already created; skip Full Name step
  const [step, setStep] = useState<0 | 1 | 2 | 3 | 4>(restaurantId ? 1 : 0)
  const [restaurantDocumentId, setRestaurantDocumentId] = useState<string | null>(restaurantId)
  const [selectedLang, setSelectedLang] = useState('en')

  // ── Step 0: Full Name ───────────────────────────────────────────────────
  const [fullName, setFullName] = useState('')
  const [savingName, setSavingName] = useState(false)

  const handleStep0 = async () => {
    if (!fullName.trim()) return
    setSavingName(true)
    try {
      await strapiPut('/api/users/me/profile', { fullName: fullName.trim() })
      updateUser({ fullName: fullName.trim() })
      setStep(1)
    } catch {
      toast.error('Failed to save name. Please try again.')
    } finally {
      setSavingName(false)
    }
  }

  const [features, setFeatureState] = useState<FeatureState>({
    sheAnswers: false,
    customerPage: false,
    pos: false,
    posMode: 'hybrid',
  })
  const toggleFeature = (key: Exclude<keyof FeatureState, 'posMode'>) =>
    setFeatureState((prev) => ({ ...prev, [key]: !prev[key] }))

  const createRestaurant = useCreateRestaurant()
  const updateRestaurant = useUpdateRestaurant()
  const createOpeningHour = useCreateOpeningHour()

  // ── Step 1: Restaurant form ─────────────────────────────────────────────
  const restaurantForm = useForm<RestaurantForm>({
    resolver: zodResolver(restaurantSchema) as any,
    defaultValues: { name: '', email: user?.email ?? '', phone: '', address: '', countryCode: '', country: '' },
  })

  const handleStep1 = async (data: RestaurantForm) => {
    try {
      const result = await createRestaurant.mutateAsync({
        bookosId: `r-${Date.now()}`,
        name: data.name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        languageCode: selectedLang,
        address: {
          address1: data.address || undefined,
          country: data.country || undefined,
          countryCode: data.countryCode || undefined,
        },
      })
      const restaurant = (result as any).data ?? result
      setRestaurantDocumentId(restaurant.documentId)
      setRestaurantId(restaurant.documentId)
      setLanguageCode(selectedLang)
      setStep(2)
    } catch {
      // error toast handled by mutation
    }
  }

  // ── Step 2: Opening hours ───────────────────────────────────────────────
  const [schedule, setSchedule] = useState<Record<string, DaySchedule>>({
    Monday:    { open: true,  openTime: '09:00', closeTime: '22:00' },
    Tuesday:   { open: true,  openTime: '09:00', closeTime: '22:00' },
    Wednesday: { open: true,  openTime: '09:00', closeTime: '22:00' },
    Thursday:  { open: true,  openTime: '09:00', closeTime: '22:00' },
    Friday:    { open: true,  openTime: '09:00', closeTime: '23:00' },
    Saturday:  { open: true,  openTime: '10:00', closeTime: '23:00' },
    Sunday:    { open: false, openTime: '10:00', closeTime: '21:00' },
  })

  const toggleDay = (day: string) =>
    setSchedule((prev) => ({ ...prev, [day]: { ...prev[day], open: !prev[day].open } }))

  const setDayTime = (day: string, field: 'openTime' | 'closeTime', value: string) =>
    setSchedule((prev) => ({ ...prev, [day]: { ...prev[day], [field]: value } }))

  const handleStep2 = async () => {
    const openDays = WEEK_DAYS.filter((d) => schedule[d.label].open)
    if (openDays.length > 0) {
      try {
        await Promise.all(
          openDays.map((d, i) => {
            const s = schedule[d.label]
            return createOpeningHour.mutateAsync({
              bookosId: `oh-${Date.now()}-${i}`,
              mode: 'booking' as any,
              day: d.value,
              name: d.label,
              open: timeToHHMM(s.openTime),
              close: timeToHHMM(s.closeTime),
              openSec: timeToSec(s.openTime),
              closeSec: timeToSec(s.closeTime),
              special: false,
              maxBookings: 0,
              maxGuests: 0,
              seatingActive: false,
              useWaitlist: false,
              paymentActive: false,
            } as any)
          }),
        )
      } catch {
        // individual errors handled by mutation toast
      }
    }
    setStep(3)
  }

  // Step 3 uses OnboardingFloorPlanEditor — no local state needed

  const finishOnboarding = async (f: FeatureState = { sheAnswers: false, customerPage: false, pos: false, posMode: 'hybrid' }) => {
    const docId = restaurantDocumentId
    if (!docId) {
      setOnboardingCompleted(true)
      navigate('/dashboard')
      return
    }
    try {
      await updateRestaurant.mutateAsync({
        documentId: docId,
        onboardingCompleted: true,
        featureSheAnswers: f.sheAnswers,
        featureCustomerPage: f.customerPage,
        featurePOS: f.pos,
        posMode: f.posMode,
      } as any)
    } catch {
      // proceed even if update fails
    }
    setOnboardingCompleted(true)
    setFeatures({ sheAnswers: f.sheAnswers, customerPage: f.customerPage, pos: f.pos, posMode: f.posMode })
    toast.success('Setup complete! Welcome aboard.')
    navigate('/dashboard')
  }

  // ── Progress indicator ──────────────────────────────────────────────────

  const totalSteps = 5

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-[var(--color-bg-base)]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(232,168,56,0.06),transparent_70%)]" />

      <div className={`relative z-10 w-full px-4 transition-all duration-300 ${step === 3 ? 'max-w-4xl' : 'max-w-lg'}`}>
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-3 mb-8">
          {Array.from({ length: totalSteps }, (_, i) => {
            const n = i + 1
            // step is 0-indexed (0-4), n is 1-indexed (1-5)
            const done = step >= n
            const active = step === n - 1
            return (
              <div key={n} className="flex items-center gap-3">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                    done || active
                      ? 'bg-[var(--color-accent-amber)] text-[#1a1714]'
                      : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]'
                  }`}
                >
                  {done ? <Check className="h-4 w-4" /> : n}
                </div>
                {n < totalSteps && (
                  <div
                    className={`h-px w-12 transition-colors ${
                      step >= n ? 'bg-[var(--color-accent-amber)]' : 'bg-[var(--color-border-subtle)]'
                    }`}
                  />
                )}
              </div>
            )
          })}
        </div>

        <AnimatePresence mode="wait">

          {/* ── STEP 0: Full Name ──────────────────────────────────────── */}
          {step === 0 && (
            <motion.div
              key="step0"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] as const }}
            >
              <Card className="p-8">
                <div className="flex flex-col items-center mb-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-[var(--radius-lg)] bg-[var(--color-accent-amber-dim)] mb-3">
                    <User className="h-6 w-6 text-[var(--color-accent-amber)]" />
                  </div>
                  <h2 className="font-display text-xl font-bold text-[var(--color-text-primary)]">
                    What's your name?
                  </h2>
                  <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                    We'll use this to personalize your experience
                  </p>
                </div>
                <div className="space-y-4">
                  <Input
                    label="Full Name"
                    placeholder="e.g. Maria Rossi"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleStep0()}
                  />
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={handleStep0}
                    disabled={!fullName.trim()}
                    loading={savingName}
                  >
                    Continue <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}

          {/* ── STEP 1: Restaurant Info ─────────────────────────────────── */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] as const }}
            >
              <Card className="p-8">
                <div className="flex flex-col items-center mb-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-[var(--radius-lg)] bg-[var(--color-accent-amber-dim)] mb-3">
                    <Building2 className="h-6 w-6 text-[var(--color-accent-amber)]" />
                  </div>
                  <h2 className="font-display text-xl font-bold text-[var(--color-text-primary)]">
                    Set up your restaurant
                  </h2>
                  <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                    Tell us about your establishment
                  </p>
                </div>

                <form onSubmit={restaurantForm.handleSubmit(handleStep1)} className="space-y-4">
                  <Input
                    label="Restaurant Name"
                    placeholder="e.g. The Golden Fork"
                    error={restaurantForm.formState.errors.name?.message}
                    {...restaurantForm.register('name')}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Email"
                      type="email"
                      placeholder="contact@restaurant.com"
                      {...restaurantForm.register('email')}
                    />
                    <Input
                      label="Phone"
                      placeholder="+1 234 567 890"
                      {...restaurantForm.register('phone')}
                    />
                  </div>
                  <Input
                    label="Address"
                    placeholder="123 Main St"
                    {...restaurantForm.register('address')}
                  />
                  <Controller
                    control={restaurantForm.control}
                    name="countryCode"
                    render={({ field }) => (
                      <CountrySelect
                        label="Country"
                        value={field.value ?? ''}
                        onChange={(code) => {
                          field.onChange(code)
                          restaurantForm.setValue('country', COUNTRIES.find((c) => c.code === code)?.name ?? '')
                        }}
                      />
                    )}
                  />
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-[var(--color-text-secondary)]">Language</label>
                    <select
                      value={selectedLang}
                      onChange={(e) => {
                        setSelectedLang(e.target.value)
                        i18next.changeLanguage(e.target.value)
                        applyRTL(e.target.value)
                      }}
                      className="w-full h-10 px-3 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-default)] text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent-amber)] cursor-pointer"
                    >
                      {LANGUAGE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    loading={createRestaurant.isPending}
                  >
                    Continue <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </form>
              </Card>
            </motion.div>
          )}

          {/* ── STEP 2: Opening Hours ───────────────────────────────────── */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] as const }}
            >
              <Card className="p-8">
                <div className="flex flex-col items-center mb-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-[var(--radius-lg)] bg-blue-500/10 mb-3">
                    <Clock className="h-6 w-6 text-blue-400" />
                  </div>
                  <h2 className="font-display text-xl font-bold text-[var(--color-text-primary)]">
                    Set your opening hours
                  </h2>
                  <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                    Toggle days you're open and set times
                  </p>
                </div>

                <div className="space-y-1 mb-6">
                  {WEEK_DAYS.map((d) => {
                    const s = schedule[d.label]
                    return (
                      <div
                        key={d.label}
                        className="flex items-center gap-3 py-2 border-b border-[var(--color-border-subtle)] last:border-0"
                      >
                        {/* Toggle switch */}
                        <button
                          type="button"
                          onClick={() => toggleDay(d.label)}
                          className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${
                            s.open ? 'bg-[var(--color-accent-amber)]' : 'bg-[var(--color-bg-elevated)]'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                              s.open ? 'translate-x-4' : 'translate-x-0'
                            }`}
                          />
                        </button>

                        {/* Day label */}
                        <span
                          className={`w-24 text-sm font-medium ${
                            s.open
                              ? 'text-[var(--color-text-primary)]'
                              : 'text-[var(--color-text-muted)]'
                          }`}
                        >
                          {d.label}
                        </span>

                        {/* Times */}
                        {s.open ? (
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="time"
                              value={s.openTime}
                              onChange={(e) => setDayTime(d.label, 'openTime', e.target.value)}
                              className="w-24 h-7 px-2 rounded-[var(--radius-sm)] bg-[var(--color-bg-elevated)] text-xs text-[var(--color-text-primary)] border border-[var(--color-border-default)] focus:outline-none focus:border-[var(--color-accent-amber)]/50"
                            />
                            <span className="text-xs text-[var(--color-text-muted)]">to</span>
                            <input
                              type="time"
                              value={s.closeTime}
                              onChange={(e) => setDayTime(d.label, 'closeTime', e.target.value)}
                              className="w-24 h-7 px-2 rounded-[var(--radius-sm)] bg-[var(--color-bg-elevated)] text-xs text-[var(--color-text-primary)] border border-[var(--color-border-default)] focus:outline-none focus:border-[var(--color-accent-amber)]/50"
                            />
                          </div>
                        ) : (
                          <span className="text-xs text-[var(--color-text-muted)] flex-1 italic">
                            Closed
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>

                <div className="flex gap-3">
                  <Button variant="ghost" className="flex-1" onClick={() => setStep(3)}>
                    Skip for now
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={handleStep2}
                    loading={createOpeningHour.isPending}
                  >
                    Save & Continue <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}

          {/* ── STEP 3: Floor Plan Editor ───────────────────────────────── */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] as const }}
            >
              <Card className="overflow-hidden">
                <div className="flex items-center gap-3 px-6 pt-6 pb-4 border-b border-[var(--color-border-subtle)]">
                  <div className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-lg)] bg-[var(--color-accent-green-dim)] shrink-0">
                    <MapPin className="h-5 w-5 text-[var(--color-accent-green)]" />
                  </div>
                  <div>
                    <h2 className="font-display text-lg font-bold text-[var(--color-text-primary)]">
                      Set up your floor plan
                    </h2>
                    <p className="text-sm text-[var(--color-text-secondary)]">
                      Add areas and drag tables to arrange your layout
                    </p>
                  </div>
                </div>
                <div className="h-[520px]">
                  <OnboardingFloorPlanEditor />
                </div>
                <div className="flex gap-3 px-6 py-4 border-t border-[var(--color-border-subtle)]">
                  <Button variant="ghost" className="flex-1" onClick={() => setStep(4)}>
                    Skip for now
                  </Button>
                  <Button className="flex-1" onClick={() => setStep(4)}>
                    Continue <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}


          {/* ── STEP 4: Features ────────────────────────────────────────── */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] as const }}
            >
              <Card className="p-8">
                <div className="flex flex-col items-center mb-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-[var(--radius-lg)] bg-purple-500/10 mb-3">
                    <Sparkles className="h-6 w-6 text-purple-400" />
                  </div>
                  <h2 className="font-display text-xl font-bold text-[var(--color-text-primary)]">
                    Enable features
                  </h2>
                  <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                    Choose the add-ons you want for your restaurant
                  </p>
                </div>
                <div className="space-y-3 mb-6">
                  <FeatureCard
                    icon={Phone}
                    iconColor="text-purple-400"
                    iconBg="bg-purple-500/10"
                    name="SheAnswers"
                    badge="AI"
                    tagline="AI voice agent that handles reservation calls automatically"
                    tooltip="SheAnswers uses ElevenLabs AI voice technology to handle incoming phone reservations on your behalf, answering questions, checking availability, and booking tables 24/7 without any human involvement."
                    enabled={features.sheAnswers}
                    onToggle={() => toggleFeature('sheAnswers')}
                  />
                  <FeatureCard
                    icon={Globe}
                    iconColor="text-blue-400"
                    iconBg="bg-blue-500/10"
                    name="Customer Page"
                    tagline="A public booking page your guests can use to reserve online"
                    tooltip="A hosted, branded web page where customers can discover your restaurant, browse availability, and make reservations directly without any third-party platform."
                    enabled={features.customerPage}
                    onToggle={() => toggleFeature('customerPage')}
                  />
                  <FeatureCard
                    icon={ShoppingCart}
                    iconColor="text-green-400"
                    iconBg="bg-green-500/10"
                    name="POS System"
                    tagline="Offline-first point-of-sale with menus, orders, and payments"
                    tooltip="A full point-of-sale system built into your dashboard. Manage your menu, take table orders, process payments, and keep operating even when offline. Syncs automatically when back online."
                    enabled={features.pos}
                    onToggle={() => toggleFeature('pos')}
                  />
                  {features.pos && (
                    <div className="grid gap-2 rounded-[var(--radius-lg)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] p-3">
                      {[
                        { value: 'hybrid' as const, label: 'Hybrid', hint: 'Use tables and counter sales from the same POS.' },
                        { value: 'integrated' as const, label: 'Tables only', hint: 'Bind POS orders to reservations and floor tables.' },
                        { value: 'standalone' as const, label: 'Standalone', hint: 'Run POS as a walk-in counter without tables.' },
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setFeatureState((prev) => ({ ...prev, posMode: option.value }))}
                          className={`flex items-center justify-between rounded-[var(--radius-md)] border px-3 py-2 text-left transition-colors ${
                            features.posMode === option.value
                              ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber-dim)]'
                              : 'border-[var(--color-border-subtle)] hover:border-[var(--color-border-default)]'
                          }`}
                        >
                          <span>
                            <span className="block text-sm font-semibold text-[var(--color-text-primary)]">{option.label}</span>
                            <span className="block text-xs text-[var(--color-text-muted)]">{option.hint}</span>
                          </span>
                          {features.posMode === option.value && <Check className="h-4 w-4 text-[var(--color-accent-amber)]" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="ghost"
                    className="flex-1"
                    onClick={() => finishOnboarding({ sheAnswers: false, customerPage: false, pos: false, posMode: 'hybrid' })}
                    loading={updateRestaurant.isPending}
                  >
                    Skip for now
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={() => finishOnboarding(features)}
                    loading={updateRestaurant.isPending}
                  >
                    Finish setup <Check className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  )
}
